// @flow
import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Button, Heading } from 'primitive-components';
import { Icon } from 'bench-internal-theme';
import { Fill, Grid, Cell } from 'layout-core-components';
import { colors, elevation } from 'bench-style-constants';

const zoomIn = keyframes`
  0% {
    transform: scale(1.1);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const StyledPosition = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const animationRule = css`
  '0.25s' ${zoomIn} forwards;
`;

const StyledContent = styled.div`
  ${({ animateIn }) =>
    animateIn &&
    css`
      animation: ${animationRule};
    `}
  padding: 0;
  border-radius: 0.5rem;
  background-color: ${colors.white};
  box-shadow: ${elevation.level3};

  ${props =>
    props.size === 'small' &&
    css`
      width: 360px;
    `}
  ${props =>
    props.size === 'medium' &&
    css`
      width: 480px;
    `}
  ${props =>
    props.size === 'large' &&
    css`
      width: 560px;
    `}
  ${props =>
    props.size === 'xLarge' &&
    css`
      width: 750px;
    `}
`;

const StyledHeader = styled.div`
  position: relative;
  padding: 20px 32px;
  border-radius: 0.5rem 0.5rem 0 0;
  background-color: ${colors.white};
`;

const StyledHeading = styled(Heading)`
  white-space: pre-line;
`;

const StyledCloseButton = styled(Button)`
  position: absolute;
  top: 20px;
  right: 32px;
  height: 32px;
  padding: 0;
`;

const StyledBody = styled.div`
  padding: 24px 32px;

  &:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }
  &:nth-child(2) {
    padding-top: 0;
  }
  &:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
`;

const StyledFooter = styled.div`
  height: 54px;
  background-color: ${colors.white};
  border-radius: 0 0 0.5rem 0.5rem;
  overflow: hidden;

  &&& button {
    display: block;
    margin: 0;
    border-radius: 0;
  }

  &&& button:not(:first-child) {
    box-shadow: inset 1px 0 0 0 ${colors.silt};
  }
`;

type ModalProps = {
  animateIn?: boolean,
  children: React.Node,
  size?: 'small' | 'medium' | 'large' | 'xLarge',
};

type ModalHeaderProps = {
  title: string,
  onClose: () => void,
  hideCloseButton?: boolean,
};

type ModalBodyProps = {
  children: React.Node,
};

type ModalFooterProps = {
  children: React.ChildrenArray<React.Element<typeof Button>>,
};

export default class Modal extends React.Component<ModalProps> {
  static Header({ title, onClose, hideCloseButton = false }: ModalHeaderProps) {
    return (
      <StyledHeader>
        <StyledHeading size={2}>{title}</StyledHeading>
        {!hideCloseButton && (
          <StyledCloseButton
            data-test-id="modal-header-close-button"
            onClick={onClose}
            styleType="neutral"
            subtle
          >
            <Icon icon="close" size="medium" />
          </StyledCloseButton>
        )}
      </StyledHeader>
    );
  }

  static Body({ children }: ModalBodyProps) {
    return <StyledBody>{children}</StyledBody>;
  }

  static Footer({ children }: ModalFooterProps) {
    return (
      <StyledFooter>
        <Fill to="parent">
          <Grid row>
            {React.Children.map(children, child => (
              <Cell size="1fr">{child}</Cell>
            ))}
          </Grid>
        </Fill>
      </StyledFooter>
    );
  }

  static defaultProps = { animateIn: false, size: 'medium' };

  render() {
    return (
      <StyledPosition>
        <StyledContent size={this.props.size} animateIn={this.props.animateIn}>
          {this.props.children}
        </StyledContent>
      </StyledPosition>
    );
  }
}
